import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { resolveContext, isSessionExpired, useSession } from '@/state/session';
import { CAPABILITIES, resolveCapability, type CapabilityId } from '@/services/entitlements';
import { RestrictedState } from '@/components/feedback';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';

/**
 * Route guards.
 *
 * Package rule (quality/SECURITY_PRIVACY_AI_AND_ACCESSIBILITY.md):
 * "Route guards and server/mock adapter checks must agree; hidden navigation
 *  alone is not security." Both this guard and the adapter enforce the same
 *  entitlement chain, so a deep link cannot reach data the nav hides.
 */

export function RequireAuth({ children }: { children: ReactNode }) {
  const phase = useSession((s) => s.phase);
  const location = useLocation();

  if (phase === 'signed-out') {
    return <Navigate to="/mobile/login" replace state={{ returnTo: location.pathname }} />;
  }
  if (phase === 'awaiting-sca') {
    return <Navigate to="/mobile/sca" replace />;
  }
  if (phase === 'awaiting-company') {
    return <Navigate to="/mobile/select-company" replace />;
  }
  if (isSessionExpired()) {
    return <Navigate to="/mobile/login" replace state={{ reason: 'session-expired', returnTo: location.pathname }} />;
  }
  return <>{children}</>;
}

/**
 * Capability guard for a whole route.
 *
 * A `hidden` denial renders a neutral restricted screen that does not confirm
 * whether the feature exists for this tenant. An `explained` denial (a
 * restricted or read-only company) states the actual reason, because the
 * requirements ask for affected features to be explained.
 */
export function RequireCapability({
  capability,
  title,
  children,
}: {
  capability: CapabilityId;
  title: string;
  children: ReactNode;
}) {
  const resolved = resolveContext();
  if (!resolved) return <Navigate to="/mobile/login" replace />;

  const decision = resolveCapability(resolved.entitlements, CAPABILITIES[capability]);
  if (decision.allowed) return <>{children}</>;

  if (decision.visibility === 'hidden') {
    return (
      <>
        <MobileHeader title="Not available" backTo="/mobile/home" backLabel="Home" />
        <RestrictedState
          title="Not available"
          message="This section is not available on your account. If you think that is wrong, ask an administrator to check your access."
        />
      </>
    );
  }

  return (
    <>
      <MobileHeader title={title} backTo="/mobile/home" backLabel="Home" />
      <RestrictedState title={title} message={decision.message} />
    </>
  );
}
